/* eslint-disable react/jsx-one-expression-per-line, jsx-a11y/label-has-associated-control */
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Modal from 'react-responsive-modal';
import { ReactComponent as PlusIcon } from '../../assets/images/vendor-plus.svg';
import { ReactComponent as FolderIcon } from '../../assets/images/folder-file.svg';
import { ReactComponent as FileIcon } from '../../assets/images/file.svg';
import { ReactComponent as ViewIcon } from '../../assets/images/view-eye.svg';
import ProteanCollapsible from '../../components/Collapsible/Collapsible';
import NewSpinnerBlock from '../../components/NewSpinnerBlock';
import PDFViewer from '../../components/PDFViewer';
import axios from '../../_actions/axiosInstance';
import config from '../../_config/config';
import { checkFeatures } from '../../helpers/utils';
import useMappingToolStore from './store';

const SelectStyled = { menuPortal: base => ({ ...base, zIndex: 9999 }) };

const formatDate = isoString => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// The backend returns a flat list (one entry per uploaded state form); the list page groups
// them by state + year, the same shape this UI has always rendered from.
const groupStateForms = stateForms => {
  const groups = new Map();

  stateForms.forEach(form => {
    const stateLabel = form.state?.abbreviation || form.state?.name || 'Unknown';
    const groupId = `${stateLabel}-${form.year}`;

    if (!groups.has(groupId)) {
      groups.set(groupId, {
        id: groupId,
        label: `${stateLabel} ${form.year}`,
        lastUpdate: null,
        files: []
      });
    }

    const group = groups.get(groupId);
    if (form.created_at && (!group.lastUpdate || form.created_at > group.lastUpdate)) {
      group.lastUpdate = form.created_at;
    }
    const { _id: id } = form;
    group.files.push({
      id,
      name: form.filename,
      version: form.version || 1,
      date: form.created_at
    });
  });

  return Array.from(groups.values())
    .map(group => ({ ...group, lastUpdate: formatDate(group.lastUpdate) }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const GroupTitle = ({ label, fileCount, lastUpdate }) => {
  const labelText = `${label} (${fileCount})`;
  const lastUpdateText = `Last update ${lastUpdate}`;
  return (
    <div className="mapping-tool-group-title">
      <FolderIcon className="mapping-tool-folder-icon" />
      <span>{labelText}</span>
      <span className="mapping-tool-group-line" />
      <span className="mapping-tool-last-update">{lastUpdateText}</span>
    </div>
  );
};

GroupTitle.propTypes = {
  label: PropTypes.string.isRequired,
  fileCount: PropTypes.number.isRequired,
  lastUpdate: PropTypes.string.isRequired
};

const FileRow = ({ id, name, version, date, onPreview }) => {
  const versionText = `Version ${version} · ${formatDate(date)}`;
  return (
    <div className="mapping-tool-file-row">
      <FileIcon className="mapping-tool-file-icon" />
      <span className="mapping-tool-file-name">{name}</span>
      <span className="mapping-tool-group-line" />
      <span className="mapping-tool-file-version">{versionText}</span>
      <button
        type="button"
        className="mapping-tool-file-preview-btn"
        aria-label={`Preview ${name}`}
        onClick={() => onPreview(id, name)}
      >
        <ViewIcon />
      </button>
    </div>
  );
};

FileRow.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  version: PropTypes.number.isRequired,
  date: PropTypes.string,
  onPreview: PropTypes.func.isRequired
};

FileRow.defaultProps = {
  date: null
};

const FormsMapperList = () => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [openGroupId, setOpenGroupId] = useState(null);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  const [stateForms, setStateForms] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [clientsError, setClientsError] = useState('');
  const [, { clearMapping }] = useMappingToolStore();

  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  // Bumped whenever a preview is opened or closed, so a response that lands after the user
  // closed the modal (or opened a different form) is dropped instead of reopening it.
  const previewRequestRef = useRef(0);

  const openPreview = (stateFormId, name) => {
    previewRequestRef.current += 1;
    const requestId = previewRequestRef.current;
    setPreviewError('');
    setPreviewTitle(name);
    setPreviewLoading(true);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }

    const url = `${config.api.api_url}${config.api.mapping_tool.getPreviewPdf(
      stateFormId
    )}?_=${Date.now()}`;
    axios
      .get(url, { responseType: 'blob' })
      .then(res => {
        if (previewRequestRef.current !== requestId) return;
        const blob = new Blob([res.data], { type: 'application/pdf' });
        setPreviewUrl(URL.createObjectURL(blob));
      })
      .catch(error => {
        if (previewRequestRef.current !== requestId) return;
        const status = error?.response?.status;
        if (status === 404) {
          setPreviewError('Mapping of the preview not found');
        } else if (status === 500) {
          setPreviewError('Preview not available. Server Error.');
        } else {
          setPreviewError('Could not load a preview for this inspection form.');
        }
      })
      .finally(() => {
        if (previewRequestRef.current !== requestId) return;
        setPreviewLoading(false);
      });
  };

  const closePreview = () => {
    // previewLoading too -- the modal's `open` is (previewUrl || previewLoading || previewError),
    // so leaving it set kept the modal on screen until the request landed, then showed the very
    // PDF the user had just dismissed, under a title that had already been cleared.
    previewRequestRef.current += 1;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewError('');
    setPreviewTitle('');
    setPreviewLoading(false);
  };

  // Reaching the list means the wizard was left -- finished, cancelled or simply navigated
  // away from. clearMapping only ran on Cancel and on a successful save, so an abandoned run
  // kept its file, details and placed fields in the singleton store and leaked into the next.
  useEffect(() => {
    clearMapping();
  }, [clearMapping]);

  useEffect(() => {
    setClientsLoading(true);
    axios
      .get(config.api.api_url + config.api.mapping_tool.getClients)
      .then(res => {
        const options = res.data.data.map(client => ({ value: client.id, label: client.name }));
        setClients(options);
        if (options.length) setSelectedClientId(options[0].value);
      })
      .catch(error => {
        // Without this the list effect never runs (no selectedClientId), nothing sets
        // listError, and the page renders its "no mapped state forms" empty state -- a
        // failed request would be indistinguishable from a client that genuinely has none.
        setClientsError(
          (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.join(', ')) ||
            'Could not load clients.'
        );
      })
      .finally(() => setClientsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClientId) return;
    setListLoading(true);
    setListError('');
    const url =
      config.api.api_url + config.api.mapping_tool.getStateFormsMappedList(selectedClientId);
    axios
      .get(url)
      .then(res => setStateForms(res.data.data))
      .catch(error => {
        setListError(
          (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.join(', ')) ||
            'Could not load mapped inspection forms.'
        );
      })
      .finally(() => setListLoading(false));
  }, [selectedClientId]);

  // Either failure means the list on screen cannot be trusted, and both read the same.
  const pageError = clientsError || listError;
  const groups = groupStateForms(stateForms);
  const totalMappedForms = stateForms.length;
  const listTitleText = `Mapped inspection forms (${totalMappedForms})`;

  const filteredGroups = groups.filter(group => {
    const term = searchTerm.toLowerCase();
    return (
      group.label.toLowerCase().includes(term) ||
      group.files.some(file => file.name.toLowerCase().includes(term))
    );
  });

  // Creating a mapping is a write; viewing the list is not. Read-only users still get
  // the list, just without the entry point into the wizard.
  const canCreateMapping = checkFeatures('mapping_tool.write');
  const goToNewMapping = () => {
    clearMapping();
    history.push('/report_template_mapper/upload');
  };

  return (
    <>
      <div className="mapping-tool-page">
        <div className="mapping-tool-header d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mapping-tool-title">Inspection Report Template Mapper - List</h1>
            <p className="mapping-tool-subtitle">List of mapped inspection report templates</p>
          </div>
          {canCreateMapping && (
            <button
              type="button"
              className="btn btn-primary mapping-tool-new-btn"
              onClick={goToNewMapping}
            >
              <PlusIcon />
              New Mapping
            </button>
          )}
        </div>

        <div className="mapping-tool-list-card">
          <div className="mapping-tool-settings-field">
            <label htmlFor="mapping-tool-client">Client</label>
            <Select
              inputId="mapping-tool-client"
              classNamePrefix="upload-form-select"
              isLoading={clientsLoading}
              isDisabled={clientsLoading || listLoading}
              options={clients}
              value={clients.find(option => option.value === selectedClientId) || null}
              onChange={option => setSelectedClientId(option?.value || '')}
              menuPortalTarget={document.body}
              styles={SelectStyled}
            />
          </div>

          {listLoading && <NewSpinnerBlock extraClass="load-block" />}

          {!listLoading && <h2 className="mapping-tool-list-title">{listTitleText}</h2>}

          {!listLoading && pageError && <p className="mapping-tool-empty-state">{pageError}</p>}

          {!listLoading && !pageError && totalMappedForms === 0 ? (
            <p className="mapping-tool-empty-state">
              No mapped inspection forms available, click in the <b>New Mapping</b> button to create
              a new mapping
            </p>
          ) : (
            !listLoading &&
            !pageError && (
              <>
                <input
                  type="text"
                  className="form-control mapping-tool-search"
                  placeholder="Search state or inspection form name"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />

                {filteredGroups.length === 0 && (
                  <p className="mapping-tool-empty-state">
                    No mapped inspection forms for this search
                  </p>
                )}

                <div className="mapping-tool-groups">
                  {filteredGroups.map(group => {
                    const fileCount = group.files.length;
                    const titleNode = (
                      <GroupTitle
                        label={group.label}
                        fileCount={fileCount}
                        lastUpdate={group.lastUpdate}
                      />
                    );
                    const toggleGroup = () =>
                      setOpenGroupId(prev => (prev === group.id ? null : group.id));
                    return (
                      <ProteanCollapsible
                        key={group.id}
                        id={group.id}
                        className="mapping-tool-group"
                        open={openGroupId === group.id}
                        onToggle={toggleGroup}
                        title={titleNode}
                      >
                        {group.files.length ? (
                          group.files.map(file => (
                            <FileRow
                              key={file.id}
                              id={file.id}
                              name={file.name}
                              version={file.version}
                              date={file.date}
                              onPreview={openPreview}
                            />
                          ))
                        ) : (
                          <p className="mapping-tool-empty">No files to display</p>
                        )}
                      </ProteanCollapsible>
                    );
                  })}
                </div>
              </>
            )
          )}
        </div>
      </div>

      <Modal
        open={Boolean(previewUrl) || previewLoading || Boolean(previewError)}
        onClose={closePreview}
        center
        focusTrapped={false}
        classNames={{ modal: 'mapping-tool-list-preview-modal' }}
      >
        <p className="mapping-tool-pdf-title">{previewTitle}</p>
        {previewLoading && <p className="mapping-tool-pdf-placeholder">Loading preview…</p>}
        {previewError && <p className="mapping-tool-pdf-placeholder">{previewError}</p>}
        {previewUrl && (
          <div className="mapping-tool-list-preview-body">
            <PDFViewer src={previewUrl} onClose={closePreview} title={previewTitle} />
          </div>
        )}
      </Modal>
    </>
  );
};

export default FormsMapperList;
